// contexts/DiscoveryContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { gameAnalyticsConfig } from '../config/gameAnalytics.js';

const DiscoveryContext = createContext(null);
const DB_NAME = 'gameAnalytics';
const STORE_NAME = 'discoveredGames';

// Browser-compatible hash function
const generateHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return hash.toString(16);
};

const initDB = async () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
    });
};

const saveToIDB = async (key, value) => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(value, key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
};

const loadFromIDB = async (key) => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
};

export const useDiscovery = () => {
    const context = useContext(DiscoveryContext);
    if (!context) {
        throw new Error('useDiscovery must be used within a DiscoveryProvider');
    }
    return context;
};

export const DiscoveryProvider = ({ children }) => {
    // Initialize settings with empty structure - will be populated from API
    const [settings, setSettings] = useState({
        filters: {},
        scoring: {}
    });
    const [defaultsLoaded, setDefaultsLoaded] = useState(false);

    // Load discovery defaults from API on mount
    useEffect(() => {
        const loadDiscoveryDefaults = async () => {
            try {
                const response = await fetch('/analytics/api/discovery/defaults');
                const data = await response.json();
                
                console.log('API response:', data); // Debug log to see actual structure
                
                // Handle V1 response format: {success: true, defaults: {...}}
                if (data.success && data.defaults) {
                    setSettings({
                        filters: { ...data.defaults.filters },
                        scoring: { ...data.defaults.scoring }
                    });
                    console.log('Loaded discovery defaults from V1 API');
                } else {
                    throw new Error('Invalid V1 response format');
                }
            } catch (error) {
                console.error('Error loading discovery defaults:', error);
                // Fallback to local config
                setSettings({
                    filters: { ...gameAnalyticsConfig.filters },
                    scoring: { ...gameAnalyticsConfig.scoring }
                });
            } finally {
                setDefaultsLoaded(true);
            }
        };
        
        loadDiscoveryDefaults();
    }, []);

    const [settingsTimestamp, setSettingsTimestamp] = useState(null); // Start as null to prevent auto-triggering
    const [discoveredGames, setDiscoveredGames] = useState([]);
    const [isDiscoveryActive, setIsDiscoveryActive] = useState(false); // Track if discovery is running
    const [discoveryProgress, setDiscoveryProgress] = useState({ processed: 0, total: 0, matching: 0 });
    const [eventSourceRef, setEventSourceRef] = useState(null); // Track EventSource at context level

    // Get settings hash for cache key
    const getSettingsHash = useCallback(() => {
        const settingsString = JSON.stringify({
            filters: settings.filters,
            scoring: settings.scoring
        });
        return generateHash(settingsString);
    }, [settings]);

    // Load saved games only after defaults are loaded
    useEffect(() => {
        if (!defaultsLoaded) return; // Wait for defaults to load first
        
        const loadSavedGames = async () => {
            try {
                const savedData = await loadFromIDB('currentGames');
                if (savedData) {
                    const { timestamp, games, settingsHash } = savedData;
                    const isSameDay = new Date(timestamp).toDateString() === new Date().toDateString();
                    const currentHash = getSettingsHash();
                    
                    // Only load if same day and same settings
                    if (isSameDay && settingsHash === currentHash) {
                        setDiscoveredGames(games);
                    }
                }
            } catch (error) {
                console.error('Error loading saved games:', error);
            }
        };
        loadSavedGames();
    }, [getSettingsHash, defaultsLoaded]);

    const setGames = useCallback(async (games) => {
        setDiscoveredGames(games);
        try {
            // Enhanced IndexedDB persistence with better error handling
            await saveToIDB('currentGames', {
                timestamp: new Date().toISOString(),
                games,
                settingsHash: getSettingsHash(),
                totalGames: games.length,
                isComplete: !isDiscoveryActive // Mark if this was a complete discovery
            });
            console.log(`Saved ${games.length} games to IndexedDB cache`);
        } catch (error) {
            console.error('Error saving games to IndexedDB:', error);
            // Don't throw - continue even if persistence fails
        }
    }, [getSettingsHash, isDiscoveryActive]);

    const updateFilters = useCallback((newFilters) => {
        setSettings(prev => ({
            ...prev,
            filters: { ...prev.filters, ...newFilters }
        }));
    }, []);

    const updateScoring = useCallback((newScoring) => {
        setSettings(prev => ({
            ...prev,
            scoring: newScoring
        }));
    }, []);

    const updatePublisherClasses = useCallback((classes) => {
        setSettings(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                publisherClasses: classes
            }
        }));
    }, []);

    const triggerDiscovery = useCallback(() => {
        console.log('Triggering discovery manually');
        // Clear previous results before starting new discovery
        setDiscoveredGames([]);
        setDiscoveryProgress({ processed: 0, total: 0, matching: 0 });
        setIsDiscoveryActive(true);
        setSettingsTimestamp(Date.now()); // Only set timestamp when user explicitly triggers
    }, []);

    const stopDiscovery = useCallback(() => {
        console.log('Manually stopping discovery');
        if (eventSourceRef) {
            eventSourceRef.close();
            setEventSourceRef(null);
        }
        setIsDiscoveryActive(false);
        setDiscoveryProgress({ processed: 0, total: 0, matching: 0 });
    }, [eventSourceRef]);

    const onDiscoveryComplete = useCallback(() => {
        setIsDiscoveryActive(false);
        setEventSourceRef(null); // Clear EventSource reference
        console.log('Discovery completed - results persisted to IndexedDB');
    }, []);

    // Cleanup EventSource on unmount (but not on tab switch)
    useEffect(() => {
        return () => {
            if (eventSourceRef) {
                console.log('Cleaning up EventSource on component unmount');
                eventSourceRef.close();
            }
        };
    }, [eventSourceRef]);

    return (
        <DiscoveryContext.Provider value={{
            settings,
            settingsTimestamp,
            discoveredGames,
            isDiscoveryActive,
            discoveryProgress,
            eventSourceRef,
            setEventSourceRef,
            setDiscoveryProgress,
            updateFilters,
            updateScoring,
            updatePublisherClasses,
            setGames,
            getSettingsHash,
            triggerDiscovery,
            stopDiscovery,
            onDiscoveryComplete
        }}>
            {children}
        </DiscoveryContext.Provider>
    );
};